import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Users, Clock, Check, ArrowRight } from "lucide-react";
import api, { money } from "@/lib/api";
import { GALLERY } from "@/constants/media";
import { Loading } from "@/components/site/ui";
import { Reveal, WordMark } from "@/components/brand";

export default function ExperienceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [exp, setExp] = useState(null);
  useEffect(() => { api.get(`/experiences/${id}`).then((r) => setExp(r.data)).catch(() => setExp(false)); }, [id]);

  if (exp === null) return <div className="pt-24"><Loading /></div>;
  if (exp === false) return <div className="pt-40 text-center"><h2 className="font-serif text-3xl">Experience not found</h2><Link to="/experiences" className="text-gold mt-4 inline-block">Back to experiences</Link></div>;

  const hero = exp.image || GALLERY[0].src;
  return (
    <div>
      <section className="relative h-[70vh] min-h-[480px] overflow-hidden noise">
        <img src={hero} alt={exp.title} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
        <div className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-6 pb-16">
          <Reveal>
            <p className="text-xs tracking-label uppercase text-pool text-glow-pool">{exp.category}</p>
            <h1 className="font-serif text-5xl sm:text-7xl mt-3">{exp.title}</h1>
          </Reveal>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-10">
          <Reveal>
            <p className="text-lg text-foreground/90 leading-relaxed">{exp.description}</p>
          </Reveal>
          <div className="flex gap-8 text-sm">
            <span className="flex items-center gap-2 text-muted-foreground"><Users size={16} className="text-gold" /> Up to {exp.capacity} guests</span>
            <span className="flex items-center gap-2 text-muted-foreground"><Clock size={16} className="text-gold" /> Priced per {exp.unit}</span>
          </div>

          {exp.amenities?.length > 0 && (
            <Reveal>
              <h3 className="font-serif text-3xl mb-5">Included</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {exp.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-3 glass rounded-md px-4 py-3">
                    <Check size={16} className="text-pool" /><span className="text-sm">{a}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {exp.addons?.length > 0 && (
            <Reveal>
              <h3 className="font-serif text-3xl mb-5">Optional Add-ons</h3>
              <div className="space-y-2">
                {exp.addons.map((a) => (
                  <div key={a.id} className="flex justify-between glass rounded-md px-4 py-3 text-sm">
                    <span>{a.name}</span>
                    <span className="text-gold">{a.charge_type === "included" ? "Included" : a.charge_type === "complimentary" ? "Complimentary" : money(a.price)}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          )}

          {exp.rules && (
            <Reveal>
              <h3 className="font-serif text-3xl mb-4">Good to know</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-line">{exp.rules}</p>
            </Reveal>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="glass rounded-lg p-7 sticky top-28">
            <WordMark size="text-2xl" subtitle={false} />
            <div className="gold-line my-5" />
            <div className="text-center">
              <p className="text-xs tracking-label uppercase text-muted-foreground">{exp.invitation_only ? "Access" : "Starting from"}</p>
              <p className="font-serif text-4xl text-gold mt-2">{exp.invitation_only ? "By Invitation" : money(exp.base_price)}</p>
              {!exp.invitation_only && <p className="text-xs text-muted-foreground mt-1">per {exp.unit} · deposit {money(exp.deposit)}</p>}
            </div>
            <button onClick={() => nav(`/book?experience=${exp.id}`)} data-testid="exp-reserve-btn"
              className="btn-gold w-full py-3.5 mt-7 text-sm flex items-center justify-center gap-2">
              Reserve This Experience <ArrowRight size={16} />
            </button>
            <p className="text-[11px] text-muted-foreground text-center mt-4">All requests are personally reviewed by your host.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
